<template>
    <v-container class="pa-0">
        <no-phone-set />
        <subscription-status />

        <skeleton-loader v-if="loading" type="coach-tabs" />

        <v-alert v-else-if="coaches.length === 0" text color="secondary" border="left" class="mt-2">
            {{ $t('company.no_coaches') }}
        </v-alert>

        <template v-else>
            <div class="coach-tabs-bar">
                <select-option-students underline no-grow :custom-options="coachOptions" :value="selectedCoachId"
                    @change="onSelectCoach" />
            </div>

            <coach-stats-row :students="studentsCount" :groups="groupsCount" :teams="teamsCount"
                :risk-count="statValue('riskCount')" :adherence="statValue('adherence30d')"
                :is-best="isBestCoach" />

            <coach-detail-panel :coach-id="selectedCoachId" :binding="binding" />
        </template>

        <company-home-panel v-if="isMobile" />

        <more-links v-if="isMobile" />
    </v-container>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { UserIndex } from "@/utils/Algolia";
import { fetchCompanyStats } from "@/utils/CompanyStats";
import NoPhoneSet from "@/components/elements/alerts/NoPhoneSet";
import SubscriptionStatus from "@/components/student/SubscriptionStatus";
import SelectOptionStudents from "@/components/coach/SelectOptionStudents";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import CoachStatsRow from "@/components/company/home/CoachStatsRow";
import CoachDetailPanel from "@/components/company/home/CoachDetailPanel";
import CompanyHomePanel from "@/components/company/home/CompanyHomePanel";
import MoreLinks from "@/components/company/home/MoreLinks";

const SELECTED_COACH_KEY = 'builder_company_home_coach'
const CACHE_TTL = 5 * 60 * 1000
const cache = { companyId: null, at: 0, coaches: null, stats: null }

const isCacheFresh = (companyId) => {
    return cache.companyId === companyId && !!cache.coaches && (Date.now() - cache.at) < CACHE_TTL
}

export default {
    name: "CompanyHome",
    components: {
        NoPhoneSet,
        SubscriptionStatus,
        SelectOptionStudents,
        SkeletonLoader,
        CoachStatsRow,
        CoachDetailPanel,
        CompanyHomePanel,
        MoreLinks,
    },
    data() {
        return {
            coaches: [],
            selectedCoachId: null,
            loading: false,
            binding: false,
            stats: null,
        }
    },
    computed: {
        ...mapState(['libraryCoach', 'groups', 'teams']),
        companyId(vm = this) {
            return vm.currentUser?.uid || null
        },
        coachOptions(vm = this) {
            return vm.coaches.map(coach => ({ name: coach.name, value: coach.id }))
        },
        selectedCoach(vm = this) {
            return vm.coaches.find(coach => coach.id === vm.selectedCoachId) || null
        },
        coachStats(vm = this) {
            if (!vm.selectedCoachId) return null
            return vm.stats?.coaches?.[vm.selectedCoachId] || null
        },
        boundToSelected(vm = this) {
            return !vm.binding && !!vm.selectedCoachId && vm.libraryCoach?.id === vm.selectedCoachId
        },
        studentsCount(vm = this) {
            const fromStats = vm.statValue('students')
            if (fromStats !== null) return fromStats
            return vm.selectedCoach ? vm.selectedCoach.students : null
        },
        groupsCount(vm = this) {
            if (vm.boundToSelected) return vm.groups.length
            return vm.statValue('groups')
        },
        teamsCount(vm = this) {
            if (vm.boundToSelected) return vm.teams.length
            return vm.statValue('teams')
        },
        isBestCoach(vm = this) {
            return !!vm.selectedCoachId && vm.stats?.totals?.bestCoachId === vm.selectedCoachId
        },
    },
    async mounted() {
        const vm = this
        if (isCacheFresh(vm.companyId)) {
            vm.coaches = cache.coaches
            vm.stats = cache.stats
            vm.restoreSelection()
            return
        }
        await Promise.all([vm.loadCoaches(), vm.loadStats()])
    },
    methods: {
        ...mapActions(['setLibraryCoach']),
        async loadCoaches() {
            const vm = this
            if (!vm.companyId) return
            vm.loading = true
            try {
                const { hits } = await UserIndex.search('', {
                    filters: `coachSubscription.companyId: ${vm.companyId}`,
                    hitsPerPage: 1000,
                })

                vm.coaches = hits
                    .filter(hit => hit?.coachSubscription?.paused !== true)
                    .map(hit => ({
                        id: hit.objectID,
                        name: hit.name || hit.email || vm._noAvailableName,
                        email: hit.email || '',
                        avatar: hit.avatar || null,
                        students: Number(hit.activeStudents || 0),
                    }))
                    .sort((a, b) => (b.students - a.students) || a.name.localeCompare(b.name))

                cache.companyId = vm.companyId
                cache.coaches = vm.coaches
                cache.at = Date.now()

                vm.restoreSelection()
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
                throw error
            } finally {
                vm.loading = false
            }
        },
        async loadStats() {
            const vm = this
            vm.stats = await fetchCompanyStats(vm.companyId)
            cache.stats = vm.stats
        },
        statValue(key) {
            const vm = this
            const value = vm.coachStats?.[key]
            return typeof value === 'number' ? value : null
        },
        restoreSelection() {
            const vm = this
            if (vm.coaches.length === 0) return

            const stored = vm.readStoredCoachId()
            const preferred = vm.libraryCoach?.id || stored
            const found = vm.coaches.find(coach => coach.id === preferred)
            vm.selectCoach(found || vm.coaches[0])
        },
        onSelectCoach(coachId) {
            const vm = this
            const coach = vm.coaches.find(item => item.id === coachId)
            if (coach) vm.selectCoach(coach)
        },
        async selectCoach(coach) {
            const vm = this
            vm.selectedCoachId = coach.id
            vm.writeStoredCoachId(coach.id)
            if (vm.libraryCoach?.id === coach.id && vm.teams.length + vm.groups.length > 0) return

            vm.binding = true
            try {
                await vm.setLibraryCoach(coach)
            } finally {
                vm.binding = false
            }
        },
        readStoredCoachId() {
            try {
                return localStorage.getItem(SELECTED_COACH_KEY)
            } catch (e) {
                return null
            }
        },
        writeStoredCoachId(coachId) {
            try {
                localStorage.setItem(SELECTED_COACH_KEY, coachId)
            } catch (e) {
                return
            }
        },
    },
}
</script>

<style scoped>
.coach-tabs-bar {
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.coach-tabs-bar::-webkit-scrollbar {
    display: none;
}
</style>
